import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./YearPlansTable.module.css";

import Select from "../_shared/Select/Select";
import YearPlanTableItem from "./YearPlanTableItem";
import Spinner from "../_shared/Spinner/Spinner";
import Error from "../_shared/Error/Error";

const YearPlansTable = ({ plansData, showShareModal, displaySharedOnly }) => {
  const [sortKey, setSortKey] = useState("updatedAt");
  const [sortAscending, setSortAscending] = useState(false);
  const [yearFilter, setYearFilter] = useState("Alle");
  const [subjectFilter, setSubjectFilter] = useState("Alle");
  const [gradeFilter, setGradeFilter] = useState("Alle");
  const { t } = useTranslation("common");

  const { data: plans, isLoading, isError } = plansData;

  function getSortMethod(key) {
    if (key === "grade")
      return (a, b) => {
        return sortAscending ? a[key] - b[key] : b[key] - a[key];
      };

    return (a, b) => {
      if (a[key] < b[key]) return sortAscending ? -1 : 1;
      else if (a[key] > b[key]) return sortAscending ? 1 : -1;
      return 0;
    };
  }

  function sortBy(key) {
    if (key !== sortKey) setSortKey(key);
    else setSortAscending((prev) => !prev);
  }

  function displayArrowWhenSortingBy(key) {
    return (
      <span className={key === sortKey ? styles.visible : ""}>
        {sortAscending ? "↑" : "↓"}
      </span>
    );
  }

  function getFilterMethod() {
    return (element) =>
      (yearFilter === "Alle" ||
        yearFilter.split(" ")[0] === String(element.year)) &&
      (subjectFilter === "Alle" ||
        element.subject?.split(";")?.some((item) => item === subjectFilter)) &&
      (gradeFilter === "Alle" || gradeFilter === String(element.grade));
  }

  function handleFilterChange(key, value) {
    switch (key) {
      case "year":
        setYearFilter(value);
        break;
      case "subject":
        setSubjectFilter(value);
        break;
      case "grade":
        setGradeFilter(value);
        break;
      default:
        return;
    }
  }

  return (
    <section className={styles.container}>
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <Error />
      ) : plans ? (
        <>
          <article className={styles.selectors__container}>
            <Select
              name={t("schoolYear")}
              options={[
                "Alle",
                ...new Set(
                  plans?.map((plan) => plan.year).sort((a, b) => a - b)
                ),
              ]}
              id={t("schoolYear")}
              currentValue={yearFilter}
              handleChange={(value) => handleFilterChange("year", value)}
            />
            <Select
              name={t("subject")}
              options={[
                "Alle",
                ...new Set(
                  plans
                    ?.map((item) =>
                      item.subject.split(";").map((subject) => subject)
                    )
                    .reduce((acc, value) => acc.concat(value), [])
                    // Sorts subjects alphabetically but puts "" at the end
                    .sort((a, b) => (a < b && a === "" ? 1 : -1))
                ),
              ]}
              id={t("subject")}
              currentValue={subjectFilter}
              handleChange={(value) => handleFilterChange("subject", value)}
            />
            <Select
              name={t("grade")}
              options={[
                "Alle",
                ...new Set(
                  plans
                    ?.map((item) => item.grade)
                    // Sorts numbers ascending but puts 0 at the end
                    .sort((a, b) => (a === 0 ? 1 : b === 0 ? -1 : a - b))
                ),
              ]}
              id={t("grade")}
              currentValue={gradeFilter}
              handleChange={(value) => handleFilterChange("grade", value)}
            />
          </article>
          <article className={styles.content}>
            <div className={styles.insetShadow} aria-hidden="true"></div>
            <section className={styles.table__container}>
              <table>
                <thead>
                  <tr>
                    <th className={styles.freeze}>
                      <button
                        className={styles.sortButton}
                        onClick={() => sortBy("title")}
                        title={`sorter basert på ${t("title")}`}
                      >
                        {t("title")}
                        {displayArrowWhenSortingBy("title")}
                      </button>
                    </th>

                    <th>
                      <button
                        className={styles.sortButton}
                        onClick={() => sortBy("year")}
                        title={`sorter basert på ${t("schoolYear")}`}
                      >
                        {t("schoolYear")}
                        {displayArrowWhenSortingBy("year")}
                      </button>
                    </th>

                    <th>
                      <button
                        className={styles.sortButton}
                        onClick={() => sortBy("subject")}
                        title={`sorter basert på ${t("subject")}`}
                      >
                        {t("subject")}
                        {displayArrowWhenSortingBy("subject")}
                      </button>
                    </th>

                    <th>
                      <button
                        className={styles.sortButton}
                        onClick={() => sortBy("grade")}
                        title={`sorter basert på ${t("grade")}`}
                      >
                        {t("grade")}
                        {displayArrowWhenSortingBy("grade")}
                      </button>
                    </th>

                    <th>
                      <button
                        className={styles.sortButton}
                        onClick={() => sortBy("updatedAt")}
                        title={`sorter basert på ${t("updatedAt")}`}
                      >
                        {t("updatedAt")}
                        {displayArrowWhenSortingBy("updatedAt")}
                      </button>
                    </th>

                    {displaySharedOnly && <th>{t("accessVia")}</th>}

                    <th>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {plans &&
                    JSON.parse(JSON.stringify(plans))
                      .filter(getFilterMethod())
                      .sort(getSortMethod(sortKey))
                      .map((item, index) => (
                        <YearPlanTableItem
                          key={index}
                          yearPlan={item}
                          showShareModal={showShareModal}
                          freezeClassName={styles.freeze}
                          displaySharedOnly={displaySharedOnly}
                        />
                      ))}
                </tbody>
              </table>
            </section>
          </article>{" "}
        </>
      ) : null}
      {plans?.filter(getFilterMethod()).length < 1 && (
        <h2 className={styles.nothingToShow}>
          {t("yearPlan:noYearPlansToShow")}
        </h2>
      )}
    </section>
  );
};

export default YearPlansTable;
